// src/pages/BusinessesPage.tsx
//
// Listagem de negócios — réplica literal do wireframe canônico
// docs/RAIOS-UX-SIDEBAR-SPEC.html, aba "04 Negócios" (correção UX v2, CW4):
// grid .biz-grid de cards .bizc (logo + upload, nome, tipo + status,
// nº demandas + nº repos). Clique no card → /businesses/:id.
//
// C2 (decisão Breno): criar negócio = botão "Novo negócio" + MODAL
// (NewBusinessModal) — BusinessWizard inline REMOVIDO desta tela.
// C4: contagens vêm do payload (business.demands_count/repos_count, W1.3) —
// sem fetch de demands/repos client-side.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type Business } from '../lib/api';
import { BusinessCard } from '@/components/business/BusinessCard';
import { BusinessDeleteDialog } from '@/components/business/BusinessDeleteDialog';
import { NewBusinessModal } from '@/components/business/NewBusinessModal';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import { Skeleton } from '@/components/ui/Skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { WfEmpty } from '@/components/wf';
import { Briefcase, Plus } from 'lucide-react';
import '@/styles/wf-biz.css';
import '@/styles/wf-skills.css'; // kebab .skl-kebab/.skl-menu do card

export function BusinessesPage() {
  const navigate = useNavigate();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showNewModal, setShowNewModal] = useState(false);
  const [deleting, setDeleting] = useState<Business | null>(null);

  const load = () => {
    setLoading(true);
    setError('');
    api.listBusinesses()
      .then(setBusinesses)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(load, []);

  // FE-5: realtime — INSERT/UPDATE/DELETE em businesses (próprios e via collab) + collaborations
  useSupabaseRealtime({ table: 'businesses' }, load);
  useSupabaseRealtime({ table: 'collaborations' }, load);

  const handleBusinessUpdated = (updated: Business) => {
    setBusinesses((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
  };

  return (
    <div>
      <PageHeader
        title="Negócios"
        description="Seus negócios no RAIOS"
        actions={
          <button type="button" className="wf-dnewbtn" onClick={() => setShowNewModal(true)}>
            <Plus className="ic" aria-hidden="true" />
            Novo negócio
          </button>
        }
        className="mb-2"
      />

      {error && (
        <p className="text-sm mb-2" style={{ color: 'var(--wf-danger-text)' }}>{error}</p>
      )}

      {loading ? (
        <div className="wf-biz-grid">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-40 rounded-lg" />
          ))}
        </div>
      ) : businesses.length === 0 ? (
        <WfEmpty icon={<Briefcase />}>
          <p>Nenhum negócio cadastrado — crie seu primeiro negócio para começar.</p>
          <button
            type="button"
            className="wf-dnewbtn"
            style={{ margin: '12px auto 0' }}
            onClick={() => setShowNewModal(true)}
          >
            <Plus className="ic" aria-hidden="true" />
            Novo negócio
          </button>
        </WfEmpty>
      ) : (
        <div className="wf-biz-grid">
          {businesses.map((biz) => (
            <BusinessCard
              key={biz.id}
              business={biz}
              onClick={(b) => navigate(`/businesses/${b.id}`)}
              onUpdated={handleBusinessUpdated}
              onError={setError}
              onDelete={setDeleting}
            />
          ))}
        </div>
      )}

      {/* Exclusão (§4.3.5): dialog dá o toast; aqui só recarrega a lista —
          a page é state-based (load), não react-query → card some sem reload. */}
      <BusinessDeleteDialog
        business={deleting}
        onClose={() => setDeleting(null)}
        onDeleted={load}
      />

      {/* C2: modal "Novo negócio" (anatomia do mockup) */}
      <NewBusinessModal
        open={showNewModal}
        onClose={() => setShowNewModal(false)}
        onCreated={(biz) => {
          setShowNewModal(false);
          navigate(`/businesses/${biz.id}`);
        }}
      />
    </div>
  );
}
